import React, { useEffect, useState } from 'react';
import {
  Table, Button, Modal, Form, Input, InputNumber, Switch, Space,
  Tag, Card, Typography, message, Tabs,
  Statistic, Row, Col, Select, DatePicker, Badge, Checkbox, Divider, Tooltip,
} from 'antd';
import {
  CrownOutlined, EditOutlined, PlusOutlined, SettingOutlined,
  CheckCircleOutlined, ClockCircleOutlined, TeamOutlined, GlobalOutlined, UnorderedListOutlined,
} from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { govtApi } from '../services/api';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.calloctech.com/api';
const BASE      = `${API_BASE_URL}/subscriptions`;

// ── helpers ────────────────────────────────────────────────────────────────
async function apiFetch(path: string, token: string, opts?: RequestInit) {
  const r = await fetch(`${BASE}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(opts?.headers ?? {}) },
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

const STATUS_COLOR: Record<string, string> = {
  free_trial:    'processing',
  premium:       'success',
  free:          'default',
  trial_expired: 'error',
};

const SubscriptionPlansPage: React.FC = () => {
  const { user } = useAuth();
  const [token, setToken] = useState('');
  const [plans, setPlans] = useState<any[]>([]);
  const [configRows, setConfigRows] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [userTotal, setUserTotal] = useState(0);
  const [userPage, setUserPage] = useState(1);
  const [userSearch, setUserSearch] = useState('');
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [planModal, setPlanModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<any>(null);
  const [userModal, setUserModal] = useState<any>(null);
  const [planForm] = Form.useForm();
  const [configForm] = Form.useForm();
  const [userForm] = Form.useForm();
  // Series-in-plan management
  const [allSeries, setAllSeries]     = useState<any[]>([]);
  const [planSeries, setPlanSeries]   = useState<any[]>([]);  // series in current plan being edited
  const [seriesLoading, setSeriesLoading] = useState(false);

  // get token
  useEffect(() => {
    user?.getIdToken().then(setToken).catch(console.error);
  }, [user]);

  const loadPlans = async () => {
    if (!token) return;
    setLoadingPlans(true);
    try { setPlans(await apiFetch('/admin/plans', token)); } catch { message.error('Failed to load plans'); }
    finally { setLoadingPlans(false); }
  };

  const loadConfig = async () => {
    if (!token) return;
    setLoadingConfig(true);
    try {
      const rows = await apiFetch('/admin/config', token);
      setConfigRows(rows);
      const vals: any = {};
      rows.forEach((r: any) => { vals[r.key] = r.value; });
      configForm.setFieldsValue(vals);
    } catch { message.error('Failed to load config'); }
    finally { setLoadingConfig(false); }
  };

  const loadUsers = async (page = 1, search = '') => {
    if (!token) return;
    setLoadingUsers(true);
    try {
      const d = await apiFetch(`/admin/users?page=${page}&limit=20&search=${encodeURIComponent(search)}`, token);
      setUsers(d.users);
      setUserTotal(d.total);
      setUserPage(page);
    } catch { message.error('Failed to load users'); }
    finally { setLoadingUsers(false); }
  };

  useEffect(() => { loadPlans(); loadConfig(); loadUsers(); }, [token]); // eslint-disable-line

  // ── Load all available series ───────────────────────────────────────────────
  const loadAllSeries = async () => {
    if (!token) return;
    try {
      const r = await govtApi.get('/govt/series');
      const list = r.data?.data ?? r.data;
      setAllSeries(Array.isArray(list) ? list : []);
    } catch { setAllSeries([]); }
  };

  const loadPlanSeries = async (planId: string) => {
    setSeriesLoading(true);
    try {
      const d = await apiFetch(`/admin/plans/${planId}/series`, token);
      setPlanSeries(d ?? []);
    } catch { setPlanSeries([]); }
    finally { setSeriesLoading(false); }
  };

  const toggleSeriesInPlan = async (planId: string, seriesId: string, checked: boolean) => {
    try {
      if (checked) {
        await apiFetch(`/admin/plans/${planId}/series`, token, { method: 'POST', body: JSON.stringify({ series_id: seriesId }) });
      } else {
        await apiFetch(`/admin/plans/${planId}/series/${seriesId}`, token, { method: 'DELETE' });
      }
      await loadPlanSeries(planId);
    } catch { message.error('Failed to update series'); }
  };

  // ── Plans ──────────────────────────────────────────────────────────────────
  const openPlanModal = (plan?: any) => {
    setEditingPlan(plan ?? null);
    planForm.setFieldsValue(plan
      ? { ...plan, features: (plan.features ?? []).join('\n') }
      : { price: 0, currency: 'INR', duration_days: 30, is_active: true, sort_order: 0, include_all_series: false }
    );
    if (plan?.id) {
      loadPlanSeries(plan.id);
    } else {
      setPlanSeries([]);
    }
    if (allSeries.length === 0) loadAllSeries();
    setPlanModal(true);
  };

  const savePlan = async () => {
    const vals = await planForm.validateFields();
    const features = (vals.features ?? '').split('\n').map((s: string) => s.trim()).filter(Boolean);
    const body = { ...vals, features };
    try {
      if (editingPlan) {
        await apiFetch(`/admin/plans/${editingPlan.id}`, token, { method: 'PUT', body: JSON.stringify(body) });
        message.success('Plan updated');
      } else {
        await apiFetch('/admin/plans', token, { method: 'POST', body: JSON.stringify(body) });
        message.success('Plan created');
      }
      setPlanModal(false);
      loadPlans();
    } catch { message.error('Save failed'); }
  };

  const planSeriesIds = new Set(planSeries.map((s: any) => s.id));

  const planColumns = [
    { title: 'Name', dataIndex: 'name', render: (v: string, r: any) => <Space><CrownOutlined style={{ color: r.price > 0 ? '#1D4ED8' : '#64748B' }} /><Text strong>{v}</Text></Space> },
    { title: 'Slug', dataIndex: 'slug', render: (v: string) => <Tag>{v}</Tag> },
    { title: 'Price', dataIndex: 'price', render: (v: number, r: any) => v > 0 ? `${r.currency} ${v}` : 'Free' },
    { title: 'Duration', dataIndex: 'duration_days', render: (v: number) => v > 0 ? `${v} days` : 'Unlimited' },
    {
      title: 'Series Access',
      render: (_: any, r: any) => r.include_all_series
        ? <Tag icon={<GlobalOutlined />} color="purple">All Series</Tag>
        : <Tag icon={<UnorderedListOutlined />} color="blue">Specific</Tag>
    },
    { title: 'Active', dataIndex: 'is_active', render: (v: boolean) => <Badge status={v ? 'success' : 'default'} text={v ? 'Active' : 'Inactive'} /> },
    {
      title: 'Actions', render: (_: any, r: any) => (
        <Button icon={<EditOutlined />} size="small" onClick={() => openPlanModal(r)}>Edit</Button>
      )
    },
  ];

  // ── Config ─────────────────────────────────────────────────────────────────
  const saveConfig = async (key: string) => {
    const val = configForm.getFieldValue(key);
    try {
      await apiFetch(`/admin/config/${key}`, token, { method: 'PUT', body: JSON.stringify({ value: String(val) }) });
      message.success(`${key} saved`);
      loadConfig();
    } catch { message.error('Save failed'); }
  };

  // ── Users ──────────────────────────────────────────────────────────────────
  const openUserModal = (rec: any) => {
    setUserModal(rec);
    userForm.setFieldsValue({
      subscription_type: rec.subscription_type,
      premium_end_date: rec.premium_end_date ? dayjs(rec.premium_end_date) : null,
    });
  };

  const saveUserSub = async () => {
    const vals = await userForm.validateFields();
    try {
      await apiFetch(`/admin/users/${userModal.id}`, token, {
        method: 'PUT',
        body: JSON.stringify({ subscription_type: vals.subscription_type, premium_end_date: vals.premium_end_date?.toISOString() ?? null }),
      });
      message.success('Updated');
      setUserModal(null);
      loadUsers(userPage, userSearch);
    } catch { message.error('Save failed'); }
  };

  const userColumns = [
    { title: 'Name', dataIndex: 'display_name', render: (v: string) => v || '—' },
    { title: 'Email', dataIndex: 'email', render: (v: string) => <Text type="secondary">{v}</Text> },
    {
      title: 'Plan', dataIndex: 'subscription_type',
      render: (v: string) => <Badge status={STATUS_COLOR[v] as any} text={v?.replace('_', ' ')} />
    },
    {
      title: 'Trial Ends', dataIndex: 'trial_end_date',
      render: (v: string) => v ? dayjs(v).format('DD MMM YYYY') : '—'
    },
    {
      title: 'Premium Ends', dataIndex: 'premium_end_date',
      render: (v: string) => v ? dayjs(v).format('DD MMM YYYY') : '—'
    },
    { title: 'Joined', dataIndex: 'created_at', render: (v: string) => dayjs(v).format('DD MMM YYYY') },
    {
      title: 'Actions', render: (_: any, r: any) => (
        <Button icon={<EditOutlined />} size="small" onClick={() => openUserModal(r)}>Manage</Button>
      )
    },
  ];

  // ── Stats cards ────────────────────────────────────────────────────────────
  const trialCount   = users.filter(u => u.subscription_type === 'free_trial').length;
  const premiumCount = users.filter(u => u.subscription_type === 'premium').length;
  const freeCount    = users.filter(u => u.subscription_type === 'free').length;

  return (
    <div>
      <Title level={3}><CrownOutlined /> Subscription Management</Title>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={8}><Card><Statistic title="On Trial" value={trialCount} prefix={<ClockCircleOutlined />} valueStyle={{ color: '#1890ff' }} /></Card></Col>
        <Col span={8}><Card><Statistic title="Premium" value={premiumCount} prefix={<CrownOutlined />} valueStyle={{ color: '#52c41a' }} /></Card></Col>
        <Col span={8}><Card><Statistic title="Free Plan" value={freeCount} prefix={<TeamOutlined />} /></Card></Col>
      </Row>

      <Tabs
        items={[
          {
            key: 'users',
            label: <span><TeamOutlined /> Users</span>,
            children: (
              <Card>
                <Space style={{ marginBottom: 16 }}>
                  <Input.Search
                    placeholder="Search by name or email"
                    onSearch={v => { setUserSearch(v); loadUsers(1, v); }}
                    style={{ width: 280 }}
                    allowClear
                  />
                  <Button onClick={() => loadUsers(userPage, userSearch)}>Refresh</Button>
                </Space>
                <Table
                  dataSource={users}
                  columns={userColumns}
                  rowKey="id"
                  loading={loadingUsers}
                  pagination={{ current: userPage, total: userTotal, pageSize: 20, onChange: p => loadUsers(p, userSearch) }}
                />
              </Card>
            ),
          },
          {
            key: 'plans',
            label: <span><CrownOutlined /> Plans</span>,
            children: (
              <Card extra={<Button type="primary" icon={<PlusOutlined />} onClick={() => openPlanModal()}>New Plan</Button>}>
                <Table dataSource={plans} columns={planColumns} rowKey="id" loading={loadingPlans} pagination={false} />
              </Card>
            ),
          },
          {
            key: 'config',
            label: <span><SettingOutlined /> App Config</span>,
            children: (
              <Card>
                <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
                  All settings take effect immediately for new requests.
                </Text>
                <Form form={configForm} layout="vertical">
                  {configRows.map(row => (
                    <Form.Item key={row.key} name={row.key} label={
                      <Space direction="vertical" size={0}>
                        <Text strong>{row.key}</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>{row.description}</Text>
                      </Space>
                    }>
                      <Space>
                        <Input style={{ width: 200 }} />
                        <Button onClick={() => saveConfig(row.key)} type="primary" size="small" icon={<CheckCircleOutlined />}>Save</Button>
                      </Space>
                    </Form.Item>
                  ))}
                </Form>
              </Card>
            ),
          },
        ]}
      />

      {/* Plan Modal */}
      <Modal
        title={editingPlan ? `Edit Plan: ${editingPlan.name}` : 'Create Plan'}
        open={planModal} onOk={savePlan} onCancel={() => setPlanModal(false)}
        width={700} okText="Save Plan"
      >
        <Tabs items={[
          {
            key: 'details',
            label: 'Plan Details',
            children: (
              <Form form={planForm} layout="vertical">
                <Row gutter={12}>
                  <Col span={12}><Form.Item name="slug" label="Slug" rules={[{ required: true }]}><Input disabled={!!editingPlan} /></Form.Item></Col>
                  <Col span={12}><Form.Item name="name" label="Display Name" rules={[{ required: true }]}><Input /></Form.Item></Col>
                </Row>
                <Row gutter={12}>
                  <Col span={8}><Form.Item name="price" label="Price (₹)"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item></Col>
                  <Col span={8}><Form.Item name="currency" label="Currency"><Input /></Form.Item></Col>
                  <Col span={8}><Form.Item name="duration_days" label="Duration (days)"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item></Col>
                </Row>
                <Row gutter={12}>
                  <Col span={8}><Form.Item name="sort_order" label="Sort Order"><InputNumber style={{ width: '100%' }} /></Form.Item></Col>
                  <Col span={8}><Form.Item name="is_active" label="Active" valuePropName="checked"><Switch /></Form.Item></Col>
                  <Col span={8}>
                    <Form.Item name="include_all_series" label="Unlock All Series" valuePropName="checked">
                      <Tooltip title="If enabled, this plan unlocks every test series automatically">
                        <Switch checkedChildren="All series" unCheckedChildren="Specific" />
                      </Tooltip>
                    </Form.Item>
                  </Col>
                </Row>
                <Form.Item name="features" label="Features (one per line)">
                  <Input.TextArea rows={4} placeholder="Unlimited practice sessions&#10;Full mock test access&#10;..." />
                </Form.Item>
              </Form>
            ),
          },
          {
            key: 'series',
            label: <span><UnorderedListOutlined /> Included Series</span>,
            disabled: !editingPlan,
            children: (
              <div>
                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                  Check the series below to include them in this plan. Users with an active subscription to this plan will unlock all checked series.
                  {' '}If "Unlock All Series" is ON above, this list is ignored.
                </Text>
                <Divider />
                {seriesLoading ? <div style={{ textAlign: 'center', padding: 24 }}>Loading…</div> : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {allSeries.filter((s: any) => !s.is_free).map((s: any) => (
                      <Card key={s.id} size="small" style={{ borderRadius: 8 }}>
                        <Row align="middle" gutter={12}>
                          <Col>
                            <Checkbox
                              checked={planSeriesIds.has(s.id)}
                              onChange={e => editingPlan && toggleSeriesInPlan(editingPlan.id, s.id, e.target.checked)}
                            />
                          </Col>
                          <Col flex={1}>
                            <Text strong>{s.title}</Text>
                            <br />
                            <Text type="secondary" style={{ fontSize: 12 }}>{s.exam_name ?? ''} · ₹{Number(s.price ?? 0).toFixed(0)}</Text>
                          </Col>
                          {planSeriesIds.has(s.id) && <Col><Tag color="green">Included</Tag></Col>}
                        </Row>
                      </Card>
                    ))}
                    {allSeries.filter((s: any) => !s.is_free).length === 0 && (
                      <Text type="secondary">No paid series found. Add paid series from the Test Series page first.</Text>
                    )}
                  </div>
                )}
              </div>
            ),
          },
        ]} />
      </Modal>

      {/* User Subscription Modal */}
      <Modal title={`Manage: ${userModal?.display_name || userModal?.email}`} open={!!userModal} onOk={saveUserSub} onCancel={() => setUserModal(null)}>
        <Form form={userForm} layout="vertical">
          <Form.Item name="subscription_type" label="Subscription Type" rules={[{ required: true }]}>
            <Select options={[
              { value: 'free_trial', label: 'Free Trial' },
              { value: 'free',       label: 'Free' },
              { value: 'premium',    label: 'Premium' },
            ]} />
          </Form.Item>
          <Form.Item name="premium_end_date" label="Premium End Date (for premium plan)">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SubscriptionPlansPage;
